import React, { useState, useEffect } from 'react';
import { analyzeResume, enhanceResume } from '../../../../services/resume.service';
import { toast } from 'react-hot-toast';
import CreditBadge from '../common/CreditBadge';

const EnhanceStep = ({ onNavigate, onDataUpdate, uploadedFile }) => {
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(true);
  const [enhancing, setEnhancing] = useState(false);

  useEffect(() => {
    if (uploadedFile) {
      performAnalysis();
    }
  }, [uploadedFile]);

  const performAnalysis = async () => {
    try {
      const result = await analyzeResume(uploadedFile.filename);
      setAnalysis(result.analysis);
      onDataUpdate({ analysis: result.analysis });
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error('Failed to analyze resume');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEnhance = async () => {
    setEnhancing(true);
    try {
      const result = await enhanceResume(uploadedFile.filename);
      onNavigate('results', {
        result,
        type: 'enhance',
        originalScore: analysis?.atsScore
      });
    } catch (error) {
      console.error('Enhancement failed:', error);
      toast.error('Failed to enhance resume');
    } finally {
      setEnhancing(false);
    }
  };

  if (!uploadedFile) {
    return (
      <div className="bg-slate-50 text-slate-800 font-inter flex flex-col min-h-screen">
        <div className="max-w-[1200px] mx-auto px-8 py-20 text-center flex-1">
          <p className="text-slate-500 mb-6 font-medium font-inter leading-relaxed text-lg">No resume uploaded. Please go back to the dashboard.</p>
          <button className="h-[52px] px-8 bg-blue-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all duration-200 hover:bg-[#0055DD] hover:-translate-y-px shadow-lg shadow-blue-500/20 mx-auto" onClick={() => onNavigate('dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const score = analysis?.atsScore || 0;
  const scoreClass = score >= 80 ? 'border-premium-green/20 bg-premium-green-light' : score >= 60 ? 'border-premium-amber/20 bg-premium-amber-light' : 'border-premium-red/20 bg-premium-red-light';
  const scoreTextColor = score >= 80 ? 'text-premium-green' : score >= 60 ? 'text-premium-amber' : 'text-premium-red';
  const scoreLabel = score >= 80 ? 'EXCELLENT' : score >= 60 ? 'GOOD' : 'NEEDS WORK';
  const scoreDesc = score >= 80 ? 'Your resume is in great shape! A few strategic tweaks could push it to perfection.' :
    score >= 60 ? 'Your resume has a solid foundation, but there are several areas where it might fail ATS filters.' :
      'Critical issues detected. Your resume likely won\'t make it past initial automated screenings.';

  return (
    <div className="bg-slate-50 text-slate-800 font-inter flex flex-col min-h-screen">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-[100]">
        <div className="max-w-[1200px] mx-auto px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-[10px] cursor-pointer" onClick={() => onNavigate('dashboard')}>
            <div className="w-[38px] h-[38px] bg-navy rounded-[10px] flex items-center justify-center"><span className="msi text-white">article</span></div>
            <span className="font-montserrat font-extrabold text-xl text-navy tracking-[-0.4px]">Accredian AI</span>
          </div>
          <div className="flex items-center gap-6">
            <CreditBadge />
          </div>
        </div>
      </nav>

      <div className="max-w-[900px] mx-auto px-8 py-10 w-full flex-1">
        <div className="inline-flex items-center gap-1.5 text-slate-400 text-[13px] font-semibold cursor-pointer transition-all hover:text-blue mb-6 font-inter underline-offset-4 hover:underline" onClick={() => onNavigate('dashboard')}>
          <span className="msi">arrow_back</span> Back to Dashboard
        </div>

        <div className="flex items-center gap-5 mb-10 animate-fade-up">
          <div className="w-14 h-14 rounded-2xl bg-premium-indigo-light text-premium-indigo flex items-center justify-center shrink-0"><span className="msi text-[32px]">trending_up</span></div>
          <div>
            <h1 className="text-[32px] font-extrabold text-navy font-montserrat mb-1">Scale your impact.</h1>
            <p className="text-[15px] text-slate-500 font-inter">We've analyzed your resume across 5 critical dimensions.</p>
          </div>
        </div>

        {analyzing ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <div className="w-14 h-14 border-4 border-blue-light border-t-blue rounded-full animate-spin mb-6"></div>
            <h3 className="text-xl font-bold text-navy font-montserrat mb-2">Analyzing your profile...</h3>
          </div>
        ) : analysis ? (
          <>
            <div className={`bg-white border rounded-3xl p-8 shadow-sm text-center mb-7 animate-fade-up ${scoreClass}`}>
              <div className={`text-[14px] font-bold uppercase tracking-widest mb-2 ${scoreTextColor}`}>{scoreLabel}</div>
              <div className="text-[48px] font-extrabold text-navy font-montserrat mb-5">{score}</div>
              <p className="text-[15px] text-slate-500 max-w-md mx-auto leading-relaxed">{scoreDesc}</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6 animate-fade-up">
              <h2 className="text-[18px] font-bold text-navy mb-5 flex items-center gap-2 font-montserrat"><span className="msi text-blue">list_alt</span> Breakdown</h2>
              <div className="flex flex-col gap-[18px]">
                {[
                  { label: 'Contact Information', val: analysis.breakdown.contactInfo },
                  { label: 'Content Completeness', val: analysis.breakdown.contentCompleteness },
                  { label: 'Formatting Quality', val: analysis.breakdown.formatting },
                  { label: 'Keyword Optimization', val: analysis.breakdown.keywords },
                  { label: 'Length & Structure', val: analysis.breakdown.length }
                ].map((item, idx) => (
                  <div key={idx} className="flex flex-col gap-2">
                    <div className="flex justify-between items-center"><span className="text-[13.5px] font-semibold text-slate-700">{item.label}</span><span className="text-[13px] font-bold text-blue">{Math.round(item.val * 100)}%</span></div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-blue to-premium-indigo" style={{ width: `${item.val * 100}%` }}></div></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-navy text-white rounded-[24px] p-10 relative overflow-hidden shadow-2xl animate-fade-up mb-20">
              <div className="flex gap-5 items-center mb-7 relative z-10">
                <div className="w-[52px] h-[52px] bg-white/10 rounded-[14px] flex items-center justify-center"><span className="msi text-[#60A5FA] text-[28px]">temp_preferences_custom</span></div>
                <div>
                  <h3 className="text-[22px] font-bold font-montserrat">One-Click Enhancement</h3>
                  <p className="text-[14px] text-slate-400">AI will fix these issues and boost your score to 90+</p>
                </div>
              </div>
              <button className="h-[52px] px-8 bg-blue-500 text-white font-bold rounded-xl w-full relative z-10" onClick={handleEnhance} disabled={enhancing}>
                {enhancing ? <><div className="w-[18px] h-[18px] border-2 border-white/35 border-t-white rounded-full animate-spin" /> Enhancing...</> : <><span className="msi">bolt</span> Enhance My Resume</>}
              </button>
            </div>
          </>
        ) : (
          <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm text-center">
            <span className="msi text-premium-red text-[48px] block mb-4">error</span>
            <h3 className="text-xl font-bold text-navy font-montserrat mb-2">Analysis Failed</h3>
            <button className="h-[52px] px-8 bg-blue-500 text-white font-bold rounded-xl mx-auto mt-6" onClick={performAnalysis}>Retry Analysis</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhanceStep;
